import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import React, { useEffect, useState } from 'react';
import { BriefcaseBusiness, Loader2, UserRoundCheck, Users } from 'lucide-react';
import { toast } from 'sonner';
import MainLayout from '../components/layout/MainLayout';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DIENSTGRAD_CONFIG } from '../lib/dienstgrade';
import { kategorienApi, wacheApi } from '../lib/api';
import { useAuthStore } from '../lib/store';

const dienstgradLabel = (dienstgrad) => (
  DIENSTGRAD_CONFIG[(dienstgrad || '').toUpperCase()]?.label
  || dienstgrad
  || 'Kein Dienstgrad hinterlegt'
);

function DienstgradAbzeichen({ dienstgrad }) {
  const config = DIENSTGRAD_CONFIG[(dienstgrad || '').toUpperCase()];
  if (!config?.image) return null;

  return (
    <div className="flex h-12 w-10 shrink-0 items-center justify-center p-1">
      <img
        src={config.image}
        alt={config.label}
        title={config.label}
        className="h-full w-full object-contain drop-shadow-sm"
      />
    </div>
  );
}

export default function SachgebietePage() {
  const canManage = useAuthStore(state => state.hasEffectivePermission('sachgebiete.manage'));
  const currentUser = useAuthStore((state) => state.user);
  const [sachgebiete, setSachgebiete] = useState([]);
  const [abteilungen, setAbteilungen] = useState([]);
  const [selectedAbteilungId, setSelectedAbteilungId] = useState('');
  const [loading, setLoading] = useState(true);
  const canSelectAbteilung = [
    'DEVELOPER',
    'WACHLEITUNG',
    'WACHLEITUNG_VERTRETUNG',
    'WACHABTEILUNGSLEITUNG',
    'ADMIN',
  ].includes(currentUser?.rolle);

  useEffect(() => {
    if (!canSelectAbteilung) {
      setSelectedAbteilungId(currentUser?.abteilung_id || '');
      return;
    }

    wacheApi.getAbteilungen()
      .then((response) => {
        const list = response.data || [];
        setAbteilungen(list);
        setSelectedAbteilungId((current) => (
          current || currentUser?.abteilung_id || list[0]?.id || ''
        ));
      })
      .catch((error) => {
        toast.error(error?.response?.data?.detail || 'Wachabteilungen konnten nicht geladen werden');
        setLoading(false);
      });
  }, [canSelectAbteilung, currentUser?.abteilung_id]);

  useEffect(() => {
    if (!selectedAbteilungId) {
      setSachgebiete([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    kategorienApi.getSachgebiete({ abteilung_id: selectedAbteilungId })
      .then((response) => setSachgebiete(response.data?.sachgebiete || []))
      .catch((error) => {
        toast.error(error?.response?.data?.detail || 'Sachgebiete konnten nicht geladen werden');
      })
      .finally(() => setLoading(false));
  }, [selectedAbteilungId]);

  const selectedAbteilung = abteilungen.find((entry) => entry.id === selectedAbteilungId);

  return (
    <MainLayout>
      <div className="space-y-4">
        <Card>
          <CardHeader className="gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div className="space-y-1.5">
              <CardTitle className="flex items-center gap-2">
              <BriefcaseBusiness className="w-5 h-5" />
              Sachgebiete
              </CardTitle>
              <CardDescription>
              Sachgebiete und ihre aktiven Mitarbeiter.
              </CardDescription>
              {selectedAbteilung?.name && (
                <p className="text-xs font-medium text-foreground">
                  Angezeigt: {selectedAbteilung.name}
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {canManage && <Button asChild variant="outline"><Link to="/mitarbeiter/sachgebiete/verwaltung">Sachgebiete verwalten</Link></Button>}
              {currentUser?.rolle === 'DEVELOPER' && <Button asChild variant="outline"><Link to="/mitarbeiter/sachgebiete/gruppenrechte">Gruppenrechte</Link></Button>}
            </div>
            {canSelectAbteilung && abteilungen.length > 0 && (
              <div className="w-full sm:w-64">
                <label className="mb-1 block text-xs font-medium text-muted-foreground">
                  Wachabteilung anzeigen
                </label>
                <Select value={selectedAbteilungId} onValueChange={setSelectedAbteilungId}>
                  <SelectTrigger data-testid="sachgebiete-abteilung-select">
                    <SelectValue placeholder="Wachabteilung auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {abteilungen.map((abteilung) => (
                      <SelectItem key={abteilung.id} value={abteilung.id}>
                        {abteilung.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </CardHeader>
        </Card>

        {loading ? (
          <div className="py-16 text-center text-muted-foreground">
            <Loader2 className="w-6 h-6 mx-auto animate-spin" />
          </div>
        ) : sachgebiete.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              Noch keine Benutzergruppe ist als Sachgebiet freigegeben.
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-4 items-start md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {sachgebiete.map((sachgebiet) => {
              const weitereMitarbeiter = sachgebiet.mitarbeiter.filter(
                (mitarbeiter) => mitarbeiter.id !== sachgebiet.hauptverantwortlicher?.id
              );
              return (
              <section key={sachgebiet.id} className="min-w-0 rounded-xl border bg-card overflow-hidden">
                <header
                  className="border-b p-4"
                  style={{ borderTop: `5px solid ${sachgebiet.farbe || '#64748b'}` }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <h2 className="font-semibold text-lg">{sachgebiet.name}</h2>
                    <Badge variant="secondary">
                      {sachgebiet.mitarbeiter.length}
                    </Badge>
                  </div>
                  {sachgebiet.beschreibung && (
                    <p className="mt-1 text-xs text-muted-foreground">{sachgebiet.beschreibung}</p>
                  )}
                </header>

                <div className="p-3 space-y-2">
                  {sachgebiet.hauptverantwortlicher ? (
                    <Card className="border-primary/40 bg-primary/5 shadow-none">
                      <CardContent className="p-3">
                        <div className="mb-2 flex items-center gap-2">
                          <UserRoundCheck className="w-4 h-4 text-primary" />
                          <Badge>Hauptverantwortlich</Badge>
                        </div>
                        <div className="flex items-center gap-3">
                          <DienstgradAbzeichen dienstgrad={sachgebiet.hauptverantwortlicher.dienstgrad} />
                          <div className="min-w-0">
                            <p className="font-semibold truncate">
                              {sachgebiet.hauptverantwortlicher.nachname},{' '}
                              {sachgebiet.hauptverantwortlicher.vorname}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {dienstgradLabel(sachgebiet.hauptverantwortlicher.dienstgrad)}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="flex items-center gap-2 rounded-lg border border-dashed p-3 text-sm text-muted-foreground">
                      <UserRoundCheck className="w-4 h-4" />
                      Noch kein Hauptverantwortlicher festgelegt
                    </div>
                  )}

                  {weitereMitarbeiter.length === 0 ? (
                    <div className="flex items-center gap-2 rounded-lg border border-dashed p-3 text-sm text-muted-foreground">
                      <Users className="w-4 h-4" />
                      Keine weiteren Mitarbeiter zugeordnet
                    </div>
                  ) : weitereMitarbeiter.map((mitarbeiter) => (
                    <Card key={mitarbeiter.id} className="shadow-none">
                      <CardContent className="p-3">
                        <div className="flex items-center gap-3">
                          <DienstgradAbzeichen dienstgrad={mitarbeiter.dienstgrad} />
                          <div className="min-w-0">
                            <p className="font-medium truncate">
                              {mitarbeiter.nachname}, {mitarbeiter.vorname}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {dienstgradLabel(mitarbeiter.dienstgrad)}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </section>
              );
            })}
          </div>
        )}
      </div>
    </MainLayout>
  );
}
